import {
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnChanges,
   Output,
   SimpleChanges,
   ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IriDataService } from 'src/app/features/stock-screener/services/iri-data-service.service';
import { AuthService } from 'src/app/shared/services/auth.service';

@Component({
    selector: 'app-community-score',
    templateUrl: './community-score.component.html',
    styleUrls: ['./community-score.component.scss'],
    standalone: false
})
export class CommunityScoreComponent implements OnChanges {
   userAverageRating: number = 0; // Initialize as 0
   // Add OnChanges interface
   @Input() communityData: any;
   errorMessage: string | null = null;
   isLoading: boolean = false;
   @Input() symbol!: string;
   @Input() symbolMainTicker!: string;
   @Output() ratingTaskCompleted = new EventEmitter<boolean>();
   hasExistingRatings: boolean = false; // To track if ratings exist
   isRemoving: boolean = false; // Separate loading state for remove action
   hasUnsavedChanges: boolean = false;
   @Input() isSample: boolean = false;
   constructor(
      private modalService: NgbModal,
      private iriReportService: IriDataService,
      private cdr: ChangeDetectorRef,
      private authService: AuthService
   ) {}

   get allCriteriaScoresZero(): boolean {
      // console.log('allCriteriaScoresZero: Checking communityData:', this.communityData);
      if (!this.communityData) {
         // console.log('allCriteriaScoresZero: communityData is null or undefined, returning true');
         return true;
      }
      const allZero =
         this.communityData.community_impact_and_reputation === 0 &&
         this.communityData.human_rights_and_labor_practices === 0 &&
         this.communityData.product_and_sector_involvement === 0 &&
         this.communityData.environmental_responsibility === 0 &&
         this.communityData.ethical_governance === 0;
      // console.log('allCriteriaScoresZero: Result:', allZero, 'communityData:', this.communityData);
      return allZero;
   }

   ngOnChanges(changes: SimpleChanges): void {
      // console.log('ngOnChanges: Changes detected:', changes);
      // console.log('ngOnChanges: allCriteriaScoresZero:', this.allCriteriaScoresZero);
      if (
         changes['communityData'] ||
         changes['symbol'] ||
         changes['symbolMainTicker']
      ) {
         this.fetchUserRating();
         // console.log('ngOnChanges: communityData:', this.communityData);
         // console.log('ngOnChanges: symbol:', this.symbol, 'symbolMainTicker:', this.symbolMainTicker);
         this.calculateUserAverage();
         this.cdr.detectChanges();
         // console.log('ngOnChanges: After fetchUserRating and calculateUserAverage, communityData:', this.communityData);
      }
   }
   getScoreClass(score: string | null): string {
      switch (score) {
         case 'Poor':
            return 'score-poor';
         case 'Weak':
            return 'score-weak';
         case 'Fair':
            return 'score-fair';
         case 'Good':
            return 'score-good';
         case 'Excellent':
            return 'score-excellent';
         default:
            return '';
      }
   }

   // MODAL
   criteria = [
      {
         title: 'Community Impact & Social Controversies',
         percentagevalue: '35%',
         description:
            'Does the company have a positive impact on the communities it operates in, while avoiding involvement in major social or political controversies?',
         score: 0,
      },
      {
         title: 'Labor Practices & Human Rights',
         percentagevalue: '25%',
         description:
            'Does the company uphold core human rights and ensure fair treatment, safety, and dignity for all workers across its operations?',
         score: 0,
      },
      {
         title: 'Product & Sector Involvement',
         percentagevalue: '20%',
         description:
            'How committed is the company to incorporating Islamic values and ethical standards in its products and sector strategies?',
         score: 0,
      },
      {
         title: 'Environmental Responsibility',
         percentagevalue: '10%',
         description:
            'To what extent does the company minimize its ecological footprint and responsibly manage environmental impacts?',
         score: 0,
      },
      {
         title: 'Ethical Governance',
         percentagevalue: '10%',
         description:
            'Does the company exhibit transparency, integrity, and accountability in its governance and decision-making processes?',
         score: 0,
      },
   ];

   @ViewChild('iriModal') iriModal: any;
   @ViewChild('cancelConfirm') cancelConfirm: any;
   @ViewChild('ThankyouModal') ThankyouModal: any;
   @ViewChild('SaveToDraftModal') SaveToDraftModal: any;
   @ViewChild('SaveDraftErrorModal') SaveDraftErrorModal: any;
   @ViewChild('removeConfirm') removeConfirm: any;
   @ViewChild('removeSuccessModal') removeSuccessModal: any;

   ngOnInit(): void {
      this.fetchInitialRatings();
      this.fetchUserRating();
      // console.log('communityData:', this.communityData.iri_score);
   }
   fetchInitialRatings(): Promise<void> {
      return this.fetchUserRating().then(() => {
         this.calculateUserAverage();
      });
   }

   getStrokeColor(score: number): string {
      switch (score) {
         case 1:
            return '#C70302';
         case 2:
            return '#FF9A3B';
         case 3:
            return '#E3D63F';
         case 4:
            return '#BFC835';
         case 5:
            return '#59A21A';
         default:
            return '#eee'; // default grey or no score
      }
   }

   calculateUserAverage(): void {
      const total = this.criteria.reduce(
         (sum, item) => sum + (item.score || 0),
         0
      );
      this.userAverageRating = this.criteria.length
         ? parseFloat((total / this.criteria.length).toFixed(1))
         : 0;
   }

   // CUSTOM SLIDER
   ratings = [1, 2, 3, 4, 5];
   radius = 21;
   circumference = 2 * Math.PI * this.radius;

   // Check if any rating has been given
   hasRatings(): boolean {
      return this.criteria.some((item) => item.score > 0);
   }

   setRating(item: any, rating: number) {
      if (item.score !== rating) {
         this.hasUnsavedChanges = true;
      }
      item.score = rating;
   }

   getProgressOffset(score: number): number {
      return this.circumference - (score / 5) * this.circumference;
   }

   // In the component class, add a new method to check if all ratings are selected
   allRatingsSelected(): boolean {
      return this.criteria.every((item) => item.score > 0);
   }

   getRatingLabel(score: number): string {
      switch (score) {
         case 1:
            return 'Poor';
         case 2:
            return 'Fair';
         case 3:
            return 'Good';
         case 4:
            return 'Very Good';
         case 5:
            return 'Excellent';
         default:
            return '';
      }
   }

   resetCriteriaScores() {
      this.criteria.forEach((item) => (item.score = 0));
   }

   // CANCEL MODAL - No changes needed here
   openCancelConfirmation(parentModal: any) {
      if (!this.hasUnsavedChanges) {
         parentModal.dismiss();
         return;
      }

      const modalRef = this.modalService.open(this.cancelConfirm, {
         modalDialogClass: 'cancel_modal_content',
      });

      modalRef.result.then(
         (result) => {
            if (result === 'confirm') {
               // Instead of resetting, fetch the original ratings again
               this.fetchUserRating().then(() => {
                  this.hasUnsavedChanges = false;
                  parentModal.dismiss();
               });
            }
         },
         () => {
            // Dismissed, do nothing
         }
      );
   }

   // THANK YOU MODAL - Updated to preserve ratings after submission
   async openThankyouModal(parentModal: any) {
      if (!this.hasRatings()) {
         this.errorMessage =
            'Please provide at least one rating before submitting';
         return;
      }

      this.isLoading = true;

      try {
         await this.submitRatings();
         parentModal.close();

         // The data is already refreshed in submitRatings()
         this.modalService.open(this.ThankyouModal, {
            modalDialogClass: 'cancel_modal_content thankyou_modal_content ',
         });

         this.ratingTaskCompleted.emit(true);
      } catch (error) {
         console.error('Submission failed:', error);
         this.errorMessage = 'Failed to submit ratings. Please try again.';
      } finally {
         this.isLoading = false;
      }
   }

   // MODAL OPENING - Ensure we always show current ratings
   openModal(content: any) {
      this.hasUnsavedChanges = false;
      // Fetch fresh ratings when opening the modal
      this.fetchUserRating().then(() => {
         this.modalService.open(content, {
            size: 'lg',
            centered: true,
            windowClass: 'iri_rating_modal',
         });
      });
   }

   private getUserId(): string {
      try {
         const userId = localStorage.getItem('userId');
         if (!userId) {
            throw new Error(
               'User ID not found in localStorage. Please login again.'
            );
         }
         return userId; // Return as string directly
      } catch (error) {
         console.error('Error getting user ID:', error);
         this.errorMessage = 'Authentication error. Please login again.';
         throw error; // Re-throw to be caught by the calling method
      }
   }

   // Method to handle rating submission
   private async submitRatings(): Promise<any> {
      try {
         const userId = this.getUserId();
         if (!userId) {
            throw new Error('Invalid user session');
         }

         const email = localStorage.getItem('email') || '';

         const response = await this.iriReportService.updateUserRating({
            user_id: userId,
            email: email,
            company_symbol: this.symbol,
            main_ticker: this.symbolMainTicker,
            is_deleted: '0',
            community_impact_and_reputation: this.criteria[0].score.toString(),
            human_rights_and_labor_practices: this.criteria[1].score.toString(),
            product_and_sector_involvement: this.criteria[2].score.toString(),
            environmental_responsibility: this.criteria[3].score.toString(),
            ethical_governance: this.criteria[4].score.toString(),
         });

         if (response?.data?.status !== 'success') {
            throw new Error(response?.data?.message || 'Submission failed');
         }

         // Refresh the data after successful submission
         await this.fetchUserRating();
         this.calculateUserAverage();
         this.cdr.detectChanges(); // Trigger change detection

         return response.data;
      } catch (error) {
         console.error('Rating submission error:', error);
         throw error;
      }
   }

   async fetchUserRating(): Promise<void> {
   // console.log('[DEBUG] fetchUserRating() - Started'); // Debug log

   if (this.authService.isLoggedIn()) {
      try {
         this.isLoading = true;
         // console.log('[DEBUG] Calling getUserRating API for:', this.symbolMainTicker); // Debug log

         const response = await this.iriReportService.getUserRating(
            this.symbolMainTicker
         );

         // console.log('[DEBUG] getUserRating API response:', response); // Debug log

         if (response?.success) {
            const data = response.data;
            const hasRatings =
               data.community_impact_and_reputation ||
               data.human_rights_and_labor_practices ||
               data.product_and_sector_involvement ||
               data.environmental_responsibility ||
               data.ethical_governance;

            this.hasExistingRatings =
               !!hasRatings && data.is_deleted !== '1';

            // console.log('[DEBUG] hasExistingRatings:', this.hasExistingRatings); // Debug log

            if (this.hasExistingRatings) {
               // console.log('[DEBUG] Setting criteria scores from API data'); // Debug log
               this.criteria[0].score = this.safeNumberConvert(
                  data.community_impact_and_reputation
               );
               this.criteria[1].score = this.safeNumberConvert(
                  data.human_rights_and_labor_practices
               );
               this.criteria[2].score = this.safeNumberConvert(
                  data.product_and_sector_involvement
               );
               this.criteria[3].score = this.safeNumberConvert(
                  data.environmental_responsibility
               );
               this.criteria[4].score = this.safeNumberConvert(
                  data.ethical_governance
               );
            } else {
               // console.log('[DEBUG] No existing ratings, resetting criteria'); // Debug log
               this.resetCriteriaScores();
            }
         } else {
            // console.log('[DEBUG] No successful response, resetting ratings'); // Debug log
            this.hasExistingRatings = false;
            this.resetCriteriaScores();
         }
      } catch (error) {
         // console.error('[DEBUG] Error in fetchUserRating:', error); // Debug log
         this.hasExistingRatings = false;
         this.resetCriteriaScores();
      } finally {
         this.isLoading = false;
         this.cdr.detectChanges();
         // console.log('[DEBUG] fetchUserRating() - Completed'); // Debug log
      }
   } else {
      // console.log('[DEBUG] User not logged in, resetting ratings'); // Debug log
      this.hasExistingRatings = false;
      this.resetCriteriaScores();
      this.cdr.detectChanges();
   }
}

   // Helper method for safe number conversion
   private safeNumberConvert(value: any): number {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
   }

   // Update communityData based on current criteria
   private updateCommunityData(): void {
      if (!this.communityData) {
         this.communityData = {};
      }

      const averageScore = this.getAverageScore();
      this.communityData.iri_score = this.hasExistingRatings
         ? averageScore
         : null;
      this.communityData.score_classification = this.hasExistingRatings
         ? this.getRatingLabel(Math.round(averageScore))
         : null;
      this.communityData.user_count = this.hasExistingRatings
         ? this.communityData.user_count || 0
         : 0;
   }

async removeRatings(parentModal: any) {
   const confirmModal = this.modalService.open(this.removeConfirm, {
      modalDialogClass: 'cancel_modal_content remove_confirm_modal_content',
   });

   confirmModal.result.then(async (result) => {
      if (result === 'confirm') {
         this.isRemoving = true;

         try {
            // First clear local state
            this.resetCriteriaScores();
            this.hasExistingRatings = false;
            this.userAverageRating = 0;

            // Call API with is_deleted: '1'
            const response = await this.iriReportService.updateUserRating({
               user_id: this.getUserId(),
               email: localStorage.getItem('email') || '',
               company_symbol: this.symbol,
               main_ticker: this.symbolMainTicker,
               is_deleted: '1',
               community_impact_and_reputation: '0',
               human_rights_and_labor_practices: '0',
               product_and_sector_involvement: '0',
               environmental_responsibility: '0',
               ethical_governance: '0'
            });

            // Force refresh of data
            await this.fetchUserRating();
            this.ratingTaskCompleted.emit(true);

            parentModal.close();

            // Open success modal with class
            this.modalService.open(this.removeSuccessModal, {
               modalDialogClass: 'cancel_modal_content remove_success_modal_content',
            });

         } catch (error) {
            console.error('Removal failed:', error);
            this.errorMessage = 'Failed to remove rating. Please try again.';
         } finally {
            this.isRemoving = false;
         }
      }
   });
}

   getAverageScore(): number {
      const total = this.criteria.reduce(
         (sum, item) => sum + (item.score || 0),
         0
      );
      return this.criteria.length
         ? parseFloat((total / this.criteria.length).toFixed(1))
         : 0;
   }

   // SAVE TO DRAFT MODAL - Updated to fix modal closing issues
   // async openSaveToDraftModal(parentModal: any) {
   //    if (!this.hasRatings()) {
   //       this.errorMessage = 'Please provide at least one rating before saving';
   //       return;
   //    }

   //    this.isLoading = true;
   //    try {
   //       const response = await this.saveRatingsAsDraft();

   //       if (response.data && response.data.status === 'success') {
   //          parentModal.close();

   //          setTimeout(() => {
   //             this.modalService.open(this.SaveToDraftModal, {
   //                modalDialogClass: 'cancel_modal_content',
   //             });

   //             // Emit event with `true` since it's completed successfully
   //             this.ratingTaskCompleted.emit(true);
   //          }, 100);
   //       }
   //    } catch (error) {
   //       console.error('Failed to save draft:', error);
   //       this.errorMessage = 'Failed to save draft. Please try again.';

   //       // Emit failure (optional)
   //       this.ratingTaskCompleted.emit(false);
   //    } finally {
   //       this.isLoading = false;
   //    }
   // }

   // Method to handle draft saving
   // private async saveRatingsAsDraft(): Promise<any> {
   //    try {
   //       const userId = this.getUserId();
   //       if (!userId) {
   //          throw new Error('Invalid user session');
   //       }
   //       const email = localStorage.getItem('email') || '';

   //       const response = await this.iriReportService.updateUserRating({
   //          user_id: userId,
   //          email: email,
   //          company_symbol: this.symbol,
   //          main_ticker: this.symbolMainTicker,
   //          is_deleted: '0',
   //          community_impact_and_reputation: this.criteria[0].score.toString(),
   //          human_rights_and_labor_practices: this.criteria[1].score.toString(),
   //          product_and_sector_involvement: this.criteria[2].score.toString(),
   //          environmental_responsibility: this.criteria[3].score.toString(),
   //          ethical_governance: this.criteria[4].score.toString(),
   //       });

   //       if (response.status !== 'success') {
   //          throw new Error(response.data?.message || 'Draft save failed');
   //       }

   //       return response; // Return the response
   //    } catch (error) {
   //       console.error('Draft save error:', error);
   //       throw error;
   //    }
   // }
}
